import React from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Container,
  Stack,
  Button,
  Fab
} from '@mui/material';
import { 
  QrCode as QrCodeIcon,
  Android as AndroidIcon,
  Apple as AppleIcon,
  Computer as ComputerIcon,
  TabletMac as TabletIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import backgroundImage from "../images/Einfahrt_B811.jpeg";

export const StartPoster = () => {
  const categories = [
    "Fahrerkarten", 
    "Umleitungen", 
    "Betriebsinfos", 
    "Schulungen", 
    "FahrPläne", 
    "FirmenHandys Schulungen"
  ];

  // PDF generation function
  const generatePDF = async () => {
    try {
      // Hide the PDF button temporarily
      const pdfButton = document.getElementById('pdf-button');
      if (pdfButton) {
        pdfButton.style.display = 'none';
      }

      // Get the container element
      const element = document.getElementById('poster-container');
      if (!element) {
        console.error('Poster container not found');
        return;
      }

      // Create canvas from the element
      const canvas = await html2canvas(element, {
        scale: 2, // Higher quality
        useCORS: true, // Allow cross-origin images
        backgroundColor: '#ffffff',
        width: element.scrollWidth,
        height: element.scrollHeight
      });

      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Calculate dimensions to fit A4
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if content is too tall
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Save the PDF
      const today = new Date();
      const dateString = today.toISOString().split('T')[0];
      pdf.save(`InfoGig-Poster-${dateString}.pdf`);

      // Show the PDF button again
      if (pdfButton) {
        pdfButton.style.display = 'block';
      }

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Fehler beim Erstellen des PDFs. Bitte versuchen Sie es erneut.');
    }
  };

  // Generate QR Code URL for the link
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent('https://infogig.link')}`;

  return (
    <Box
      id="poster-container"
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(rgba(255,255,255,0.1), rgba(255,255,255,0.3)), url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        position: 'relative'
      }}
    >
      {/* Floating PDF Button */}
      <Fab
        id="pdf-button"
        color="primary"
        aria-label="PDF erstellen"
        onClick={generatePDF}
        sx={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 9999,
          backgroundColor: '#d32f2f',
          '&:hover': {
            backgroundColor: '#b71c1c'
          },
          boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
        }}
      >
        <PdfIcon />
      </Fab>
      <Container 
        maxWidth="lg" 
        sx={{ 
          position: 'relative', 
          zIndex: 2, 
          py: 4,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh'
        }}
      >
        {/* Top Section - Categories */}
        <Box sx={{ mb: 4 }}>
          <Card 
            sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.95)', 
              borderRadius: 3, 
              boxShadow: 4,
              mb: 1
            }}
          >
            <CardContent sx={{ p: 0.5 }}>
              <Typography 
                variant="h5" 
                component="h3" 
                sx={{ 
                  textAlign: 'center',
                  color: '#1976d2',
                  fontWeight: 'bold',
                  mb: 0.3,
                  fontSize: { xs: '1rem', sm: '1rem', md: '1.2rem' }
                }}
              >
                InfoGig Services
              </Typography>
              
              <Grid container spacing={0.5} justifyContent="center">
                {categories.map((category, index) => (
                  <Grid item xs={12} sm={6} md={2} key={index}>
                    {/* Bus-shaped card for all categories */}
                    <Box sx={{ position: 'relative', height: '60px' }}>
                      <Card 
                        sx={{ 
                          backgroundColor: 'rgba(25, 118, 210, 0.1)',
                          border: '2px solid #1976d2',
                          borderRadius: '15px 15px 3px 3px',
                          transition: 'all 0.3s ease',
                          height: '45px',
                          position: 'relative',
                          overflow: 'visible',
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.2)',
                            transform: 'translateY(-2px)',
                            boxShadow: 3
                          }
                        }}
                      >
                        {/* Bus front windshield */}
                        <Box
                          sx={{
                            position: 'absolute',
                            right: '-1px',
                            top: '2px',
                            width: '8px',
                            height: '25px',
                            backgroundColor: '#1976d2',
                            borderRadius: '0 7px 0px 0',
                            zIndex: 2
                          }}
                        />
                        
                        {/* Bus wheels */}
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: '-8px',
                            left: '15%',
                            width: '16px',
                            height: '16px',
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            borderRadius: '50%',
                            border: '2px solid #1976d2',
                            zIndex: 3
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: '-8px',
                            right: '15%',
                            width: '16px',
                            height: '16px',
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            borderRadius: '50%',
                            border: '2px solid #1976d2',
                            zIndex: 3
                          }}
                        />
                        
                        <Box sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '45px',
                          width: '100%'
                        }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' },
                              fontWeight: 'bold',
                              color: '#1976d2',
                              textShadow: '1px 1px 2px rgba(255, 255, 255, 0.8)',
                              textAlign: 'center',
                              lineHeight: 1.2,
                              margin: 0,
                              padding: '4px 12px'
                            }}
                          >
                            {category}
                          </Typography>
                        </Box>
                      </Card>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Box>

        

        {/* Bottom Section - Platform Icons and QR Code */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'stretch', mb: 2, gap: 3 }}>
          {/* Bottom Left - Platform Icons */}
          <Card 
            sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.95)', 
              borderRadius: 3, 
              boxShadow: 4,
              flex: 1,
              maxWidth: 400,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#1976d2',
                    fontWeight: 'bold',
                    mb: 2,
                    textAlign: 'center'
                  }}
                >
                  Verfügbar auf allen Plattformen
                </Typography>
                
                <Grid container spacing={2} justifyContent="center">
                  <Grid item>
                    <Stack alignItems="center" spacing={1}>
                      <AndroidIcon sx={{ fontSize: 40, color: '#4CAF50' }} />
                      <Typography variant="body2" color="text.secondary">
                        Android
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item>
                    <Stack alignItems="center" spacing={1}>
                      <AppleIcon sx={{ fontSize: 40, color: '#424242' }} />
                      <Typography variant="body2" color="text.secondary">
                        iOS
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item>
                    <Stack alignItems="center" spacing={1}>
                      <ComputerIcon sx={{ fontSize: 40, color: '#2196F3' }} />
                      <Typography variant="body2" color="text.secondary">
                        Desktop
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item>
                    <Stack alignItems="center" spacing={1}>
                      <TabletIcon sx={{ fontSize: 40, color: '#FF9800' }} />
                      <Typography variant="body2" color="text.secondary">
                        Tablet
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>

          {/* Bottom Right - QR Code */}
          <Card 
            sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.95)', 
              borderRadius: 3, 
              boxShadow: 4,
              flex: 1,
              maxWidth: 400,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Stack spacing={2} alignItems="center" sx={{ flex: 1, justifyContent: 'center' }}>
                
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#1976d2',
                    fontWeight: 'bold'
                  }}
                >
                  QR-Code Zugang
                </Typography>
                
                <Box
                  component="img"
                  src={qrCodeUrl}
                  alt="QR Code für infogig.link"
                  sx={{
                    width: 150,
                    height: 150,
                    border: '2px solid #1976d2',
                    borderRadius: 2,
                    backgroundColor: 'white'
                  }}
                />
                
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary',
                    fontFamily: 'monospace',
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    padding: '4px 8px',
                    borderRadius: 1,
                    fontSize: '0.8rem'
                  }}
                >
                  infogig.link
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Bottom Card with Background Image */}
        <Card 
          sx={{ 
            marginTop: 4,
            backgroundColor: 'rgba(255, 255, 255, 0.95)', 
            borderRadius: 3, 
            boxShadow: 4,
            overflow: 'hidden',
            padding: 2
          }}
        >
          <Box
            sx={{
              position: 'relative',
              height: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            {/* Background Image as img element for printing */}
            <Box
              component="img"
              src={backgroundImage}
              alt="InfoGig Background"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                zIndex: 0,
                // Ensure image prints
                '@media print': {
                  display: 'block !important',
                  visibility: 'visible !important',
                  opacity: '1 !important'
                }
              }}
            />
            
            {/* Dark overlay for better text visibility */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                borderRadius: 2,
                zIndex: 1
              }}
            />
            
            {/* Content over the image */}
            <Box sx={{ position: 'relative', textAlign: 'center', zIndex: 2 }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  color: 'white',
                  fontWeight: 'bold',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                  mb: 1,
                  '@media print': {
                    color: 'black !important',
                    textShadow: 'none !important'
                  }
                }}
              >
                InfoGig
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'white',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                  '@media print': {
                    color: 'black !important',
                    textShadow: 'none !important'
                  }
                }}
              >
                Ihr digitaler Arbeitsplatz
              </Typography>
            </Box>
          </Box>
        </Card>
      </Container>
    </Box>
  );
};

export default StartPoster;
